/**
 * Page /grc/services — back-office « Services & vie quotidienne » (GRC).
 *
 * Écran d'ensemble des services pratiques citoyens rattachés au socle GRC.
 * Objectif produit (validé) : la mairie voit TOUJOURS tout ce qui est possible,
 * même non configuré, pour ne jamais oublier qu'un service peut être activé —
 * alors que côté administré, un service vide est masqué automatiquement.
 *
 * Chaque service affiche son état : actif (contenu saisi, visible au public) /
 * à configurer (jamais rempli) / en cours. Premier lot : Déchets, pleinement
 * géré ici (secteurs + jours de collecte). Les suivants (objets trouvés,
 * cimetière, RDV) apparaissent en « bientôt » puis deviendront gérables.
 *
 * Conforme au guide des briques : GrcLayout (AppHeader + GrcNav), zéro
 * window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, SectionEyebrow, Input, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type {
  DechetsSecteur, DechetsCollecte, DechetsEtatMairie, DechetFlux, DechetFrequence,
  ObjetsEtatMairie, RdvEtatMairie, CimetiereEtatMairie,
} from '@/lib/api';
import { ObjetsTrouvesManager } from '@/components/grc/ObjetsTrouvesManager';
import { RdvManager } from '@/components/grc/RdvManager';
import { CimetiereManager } from '@/components/grc/CimetiereManager';
import { CollecteManager } from '@/components/grc/CollecteManager';
import {
  Trash2, Search, Calendar, MapPin, Plus, Pencil, Eye, EyeOff, AlertCircle, Recycle,
} from 'lucide-react';

const FLUX_LABEL: Record<DechetFlux, string> = {
  om: 'Ordures ménagères',
  tri: 'Tri sélectif (bac jaune)',
  verre: 'Verre',
  vegetaux: 'Déchets végétaux',
  encombrants: 'Encombrants',
};
const FLUX_OPTIONS: DechetFlux[] = ['om', 'tri', 'verre', 'vegetaux', 'encombrants'];
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const FREQ_LABEL: Record<DechetFrequence, string> = {
  hebdo: 'Chaque semaine',
  paire: 'Semaines paires',
  impaire: 'Semaines impaires',
};

/** Carte d'état d'un service pratique (déchets, objets trouvés…). */
function ServiceCard({
  icon, titre, description, etat, children,
}: {
  icon: React.ReactNode;
  titre: string;
  description: string;
  etat: 'actif' | 'a_configurer' | 'bientot';
  children?: React.ReactNode;
}) {
  const badge =
    etat === 'actif' ? <Badge variant="success" size="sm">Actif · visible au public</Badge>
    : etat === 'a_configurer' ? <Badge variant="warning" size="sm">À configurer</Badge>
    : <Badge variant="neutral" size="sm">Bientôt</Badge>;
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-r-md bg-grc-tint text-grc-fg">
            {icon}
          </div>
          <div>
            <div className="font-medium text-ink">{titre}</div>
            <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
          </div>
        </div>
        {badge}
      </div>
      {children && <div className="mt-4 border-t border-border pt-4">{children}</div>}
    </Card>
  );
}

export default function GrcServicesPage() {
  const { confirm, dialogElement } = useConfirmDialog();

  const [etat, setEtat] = useState<DechetsEtatMairie | null>(null);
  const [objetsEtat, setObjetsEtat] = useState<ObjetsEtatMairie | null>(null);
  const [rdvEtat, setRdvEtat] = useState<RdvEtatMairie | null>(null);
  const [cimetiereEtat, setCimetiereEtat] = useState<CimetiereEtatMairie | null>(null);
  const [collecteActif, setCollecteActif] = useState(false);
  const [secteurs, setSecteurs] = useState<DechetsSecteur[]>([]);
  const [collectes, setCollectes] = useState<DechetsCollecte[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [secteurEdit, setSecteurEdit] = useState<DechetsSecteur | 'new' | null>(null);
  const [collecteEdit, setCollecteEdit] = useState<DechetsCollecte | 'new' | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [e, s, c] = await Promise.all([
        api.grc.dechets.etat(),
        api.grc.dechets.listSecteurs(),
        api.grc.dechets.listCollectes(),
      ]);
      setEtat(e);
      setSecteurs(s.secteurs);
      setCollectes(c.collectes);
      // État objets trouvés (pour le badge de la carte) — non bloquant.
      try { setObjetsEtat(await api.grc.objets.etat()); } catch { /* ignore */ }
      // État RDV (pour le badge de la carte) — non bloquant.
      try { setRdvEtat(await api.grc.rdv.etat()); } catch { /* ignore */ }
      // État cimetière (pour le badge de la carte) — non bloquant.
      try { setCimetiereEtat(await api.grc.cimetiere.etat()); } catch { /* ignore */ }
      // État points de collecte (badge) — actif si au moins un point OU une déchetterie.
      try {
        const [pts, d] = await Promise.all([api.grc.collecte.listPoints(), api.grc.collecte.getDechetterie()]);
        setCollecteActif(pts.points.length > 0 || d !== null);
      } catch { /* ignore */ }
    } catch (err) {
      setError(err instanceof ApiException ? err.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function supprimerSecteur(s: DechetsSecteur) {
    const ok = await confirm({
      title: 'Supprimer ce secteur ?',
      description: `« ${s.nom} » et ses jours de collecte seront définitivement supprimés.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.dechets.removeSecteur(s.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  async function supprimerCollecte(c: DechetsCollecte) {
    const ok = await confirm({
      title: 'Supprimer ce jour de collecte ?',
      description: `${FLUX_LABEL[c.flux]} — ${JOURS[c.jour]} sera supprimé.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.grc.dechets.removeCollecte(c.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  const etatDechets: 'actif' | 'a_configurer' =
    etat && etat.actif ? 'actif' : 'a_configurer';

  return (
    <GrcLayout active="dechets">
      <SectionEyebrow as="div">Services &amp; vie quotidienne</SectionEyebrow>
      <h1 className="mt-1 font-display text-2xl font-semibold text-ink">
        Services pratiques de la commune
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-ink-muted">
        Ces services apparaissent aux administrés dans « Infos, alertes &amp; services ».
        Un service non rempli reste masqué au public, mais vous le voyez toujours ici pour
        pouvoir l'activer quand vous êtes prêt.
      </p>

      {error && <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>}

      {loading ? (
        <p className="mt-8 text-sm text-ink-muted">Chargement…</p>
      ) : (
        <div className="mt-6 space-y-4">
          {/* ── DÉCHETS ─────────────────────────────────────────────────── */}
          <ServiceCard
            icon={<Trash2 className="h-5 w-5" />}
            titre="Collecte des déchets"
            description="Calendrier de collecte par secteur + rappel push la veille pour les habitants."
            etat={etatDechets}
          >
            {/* Secteurs */}
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink">Secteurs de collecte</h2>
              <Button variant="ghost" size="sm" onClick={() => setSecteurEdit('new')}>
                <Plus className="h-4 w-4" /> Secteur
              </Button>
            </div>
            {secteurs.length === 0 ? (
              <p className="mt-2 text-sm text-ink-muted">
                Aucun secteur. Créez-en un (ex. « Bourg ») puis ajoutez les jours de collecte.
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {secteurs.map((s) => (
                  <li key={s.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-medium text-ink">
                          <MapPin className="h-4 w-4 text-grc" aria-hidden="true" />
                          {s.nom}
                          {!s.affiche && (
                            <span className="inline-flex items-center gap-1 text-xs text-ink-dim">
                              <EyeOff className="h-3 w-3" /> masqué
                            </span>
                          )}
                        </div>
                        {s.description && <p className="mt-0.5 text-sm text-ink-muted">{s.description}</p>}
                        {/* Collectes de ce secteur */}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {collectes.filter((c) => c.secteurId === s.id).map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setCollecteEdit(c)}
                              className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2 py-0.5 text-xs text-ink-muted hover:border-grc/40 hover:text-ink"
                              title="Modifier ce jour de collecte"
                            >
                              <Calendar className="h-3 w-3" />
                              {FLUX_LABEL[c.flux]} · {JOURS[c.jour]}
                              {c.frequence !== 'hebdo' && ` (${FREQ_LABEL[c.frequence].toLowerCase()})`}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setCollecteEdit('new')} title="Ajouter un jour">
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSecteurEdit(s)} title="Modifier">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => supprimerSecteur(s)} title="Supprimer">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ServiceCard>

          {/* ── Services suivants (aperçu — activés aux lots suivants) ───── */}
          <ServiceCard
            icon={<Search className="h-5 w-5" />}
            titre="Objets trouvés"
            description="Registre des objets trouvés (photo, lieu, statut à réclamer/rendu)."
            etat={objetsEtat && objetsEtat.actif ? 'actif' : 'a_configurer'}
          >
            <ObjetsTrouvesManager onEtatChange={() => { void reload(); }} />
          </ServiceCard>
          <ServiceCard
            icon={<MapPin className="h-5 w-5" />}
            titre="Cimetière / concessions"
            description="Registre des concessions, recherche de défunt, échéances de renouvellement."
            etat={cimetiereEtat && cimetiereEtat.actif ? 'actif' : 'a_configurer'}
          >
            <CimetiereManager onEtatChange={() => { void reload(); }} />
          </ServiceCard>
          <ServiceCard
            icon={<Calendar className="h-5 w-5" />}
            titre="Prise de rendez-vous en mairie"
            description="Créneaux CNI/passeport et autres motifs, avec lien vers la pré-demande ANTS."
            etat={rdvEtat && rdvEtat.actif ? 'actif' : 'a_configurer'}
          >
            <RdvManager onEtatChange={() => { void reload(); }} />
          </ServiceCard>
          <ServiceCard
            icon={<Recycle className="h-5 w-5" />}
            titre="Points de collecte & déchetterie"
            description="Containers (verre, textile, piles…) sur une carte + fiche déchetterie et dossier d'inscription."
            etat={collecteActif ? 'actif' : 'a_configurer'}
          >
            <CollecteManager onEtatChange={() => { void reload(); }} />
          </ServiceCard>
        </div>
      )}

      {/* ── Modale secteur ──────────────────────────────────────────────── */}
      {secteurEdit && (
        <SecteurDialog
          initial={secteurEdit === 'new' ? null : secteurEdit}
          onClose={() => setSecteurEdit(null)}
          onSaved={async () => { setSecteurEdit(null); await reload(); }}
        />
      )}

      {/* ── Modale collecte ─────────────────────────────────────────────── */}
      {collecteEdit && (
        <CollecteDialog
          initial={collecteEdit === 'new' ? null : collecteEdit}
          secteurs={secteurs}
          onClose={() => setCollecteEdit(null)}
          onSaved={async () => { setCollecteEdit(null); await reload(); }}
          onDelete={collecteEdit !== 'new' ? () => { const c = collecteEdit; setCollecteEdit(null); void supprimerCollecte(c); } : undefined}
        />
      )}

      {dialogElement}
    </GrcLayout>
  );
}

// ============================================================================
// Modale — Secteur
// ============================================================================

function SecteurDialog({
  initial, onClose, onSaved,
}: {
  initial: DechetsSecteur | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nom, setNom] = useState(initial?.nom ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [affiche, setAffiche] = useState(initial?.affiche ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = { nom: nom.trim(), description: description.trim() || null, affiche };
      if (initial) await api.grc.dechets.updateSecteur(initial.id, body);
      else await api.grc.dechets.createSecteur(body);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible');
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier le secteur' : 'Nouveau secteur'}>
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Nom du secteur" htmlFor="sec-nom" required>
          <Input id="sec-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Bourg, Rives de Charente…" />
        </Field>
        <Field label="Rues concernées (optionnel)" htmlFor="sec-desc">
          <textarea
            id="sec-desc"
            className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Rue de l'Église, Place du Bastion…"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={affiche} onChange={(e) => setAffiche(e.target.checked)} />
          {affiche ? <Eye className="h-4 w-4 text-grc" /> : <EyeOff className="h-4 w-4 text-ink-dim" />}
          Visible par les administrés
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy || !nom.trim()}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ============================================================================
// Modale — Collecte
// ============================================================================

function CollecteDialog({
  initial, secteurs, onClose, onSaved, onDelete,
}: {
  initial: DechetsCollecte | null;
  secteurs: DechetsSecteur[];
  onClose: () => void;
  onSaved: () => void;
  onDelete?: () => void;
}) {
  const [secteurId, setSecteurId] = useState(initial?.secteurId ?? secteurs[0]?.id ?? '');
  const [flux, setFlux] = useState<DechetFlux>(initial?.flux ?? 'om');
  const [jour, setJour] = useState<number>(initial?.jour ?? 0);
  const [frequence, setFrequence] = useState<DechetFrequence>(initial?.frequence ?? 'hebdo');
  const [consigne, setConsigne] = useState(initial?.consigne ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!secteurId) { setErr('Choisissez un secteur (créez-en un d\'abord).'); return; }
    setBusy(true); setErr(null);
    try {
      const body = { secteurId, flux, jour, frequence, consigne: consigne.trim() || null };
      if (initial) await api.grc.dechets.updateCollecte(initial.id, body);
      else await api.grc.dechets.createCollecte(body);
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiException ? e.message : 'Enregistrement impossible');
      setBusy(false);
    }
  }

  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  return (
    <Dialog open onClose={onClose} title={initial ? 'Modifier le jour de collecte' : 'Nouveau jour de collecte'}>
      <div className="space-y-3">
        {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
        <Field label="Secteur" htmlFor="col-secteur" required>
          <select id="col-secteur" className={selectCls} value={secteurId} onChange={(e) => setSecteurId(e.target.value)}>
            {secteurs.length === 0 && <option value="">— aucun secteur —</option>}
            {secteurs.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </Field>
        <Field label="Type de déchet" htmlFor="col-flux" required>
          <select id="col-flux" className={selectCls} value={flux} onChange={(e) => setFlux(e.target.value as DechetFlux)}>
            {FLUX_OPTIONS.map((f) => <option key={f} value={f}>{FLUX_LABEL[f]}</option>)}
          </select>
        </Field>
        <Field label="Jour de collecte" htmlFor="col-jour" required>
          <select id="col-jour" className={selectCls} value={jour} onChange={(e) => setJour(Number(e.target.value))}>
            {JOURS.map((j, i) => <option key={i} value={i}>{j}</option>)}
          </select>
        </Field>
        <Field label="Fréquence" htmlFor="col-freq" required>
          <select id="col-freq" className={selectCls} value={frequence} onChange={(e) => setFrequence(e.target.value as DechetFrequence)}>
            <option value="hebdo">{FREQ_LABEL.hebdo}</option>
            <option value="paire">{FREQ_LABEL.paire}</option>
            <option value="impaire">{FREQ_LABEL.impaire}</option>
          </select>
        </Field>
        <Field label="Consigne (optionnel)" htmlFor="col-consigne">
          <Input id="col-consigne" value={consigne} onChange={(e) => setConsigne(e.target.value)} placeholder="Sortir la veille au soir" />
        </Field>
        <div className="flex items-center justify-between gap-2 pt-2">
          {onDelete
            ? <Button variant="ghost" onClick={onDelete} disabled={busy}><Trash2 className="h-4 w-4" /> Supprimer</Button>
            : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
            <Button variant="primary" onClick={save} disabled={busy || !secteurId}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
